/**
 * Metric condition in COMPARE mode — the threshold is another metric over
 * its own window instead of a fixed number. Reads as a sentence:
 *
 *   [ Metric ▼ ] [ is ▼ ]                                   [#] [🗑]
 *   COMPARED TO ( This row | the TOTAL )’s [ Metric ▼ ] over [ Window ▼ ]
 *   "Matches when CPuC is below the TOTAL CPuC over Last 7 days."
 *
 * ONE row for BOTH tag surfaces (FO tag rules and performance tags): the
 * condition type is structural — anything with field/operator/compareTo —
 * and extra fields (FO's `kind`, perf's `value`) ride through the spreads
 * untouched. The plain-English line under the controls is ALWAYS on: it is
 * what makes the row self-explanatory. The [#] icon returns to the classic
 * editor with the old value intact. The % stays behind SHOW_COMPARE_PERCENT.
 * For Media Devoted | Created By Ciro Autiero
 */

import { Hash, Trash2 } from 'lucide-react';
import type {
  TagCampaignThreshold,
  TagCompareTo,
  TagCountryThreshold,
} from '@trafficpulse/shared';

import { Button } from '@/components/ui';
import { getStatsDateRangeLabel } from '@/components/date-range';
import type { StatsDateRangePreset } from '@/components/date-range';
import CompareHelpLine from '@/features/flow-optimizer/compare/CompareHelpLine';
import { compareCalcTitle } from '@/features/flow-optimizer/compare/compare-example';
import { TAG_OP_TO_CANONICAL, compareHelp } from '@/features/flow-optimizer/compare/compare-sentence';
import { LIST_METRIC_FIELDS, OPERATORS } from '@/pages/admin/ListTagRulesPage/constants';

import CompareOverrideTables from './CompareOverrideTables';
import AgainstToggle from './controls/AgainstToggle';
import CompareCustomBounds from './controls/CompareCustomBounds';
import CompareTargetGroup, { isDropScoped } from './controls/CompareTargetGroup';
import PercentField from './controls/PercentField';

export interface CompareableCondition {
  field: string;
  operator: string;
  compareTo?: TagCompareTo;
  countryThresholds?: TagCountryThreshold[];
  campaignThresholds?: TagCampaignThreshold[];
}

export interface CompareConditionRowProps<T extends CompareableCondition> {
  condition: T;
  onChange: (c: T) => void;
  onRemove: () => void;
  canRemove: boolean;
  /** Selectable metric fields; default = the list stats columns. */
  fields?: ReadonlyArray<{ key: string; label: string }>;
}

const SELECT =
  'h-9 w-full min-w-0 rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring';
const LABEL = 'mb-1 block text-xs font-medium text-muted-foreground';

/** Named-window label for the sentence; undefined = the rule's own window. */
function windowLabelOf(ct: TagCompareTo): string | undefined {
  if (!ct.dateRangePreset) return undefined;
  if (ct.dateRangePreset !== 'custom') {
    return getStatsDateRangeLabel(ct.dateRangePreset as StatsDateRangePreset);
  }
  return `${ct.fromDate || '?'} → ${ct.toDateIsToday ? 'today' : ct.toDate || '?'}`;
}

export function CompareConditionRow<T extends CompareableCondition>({
  condition,
  onChange,
  onRemove,
  canRemove,
  fields = LIST_METRIC_FIELDS,
}: CompareConditionRowProps<T>) {
  const ct = condition.compareTo ?? { field: condition.field };
  // The spreads keep every field this row doesn't know about (kind, value,
  // …) — the cast is safe because only compare fields are overridden.
  const patchCt = (next: TagCompareTo) =>
    onChange({ ...condition, compareTo: next } as T);
  const labelOf = (key: string): string =>
    fields.find((f) => f.key === key)?.label ?? key;
  const lhsLabel = labelOf(condition.field);
  const rhsLabel =
    (ct.against === 'total' ? 'TOTAL ' : '') + labelOf(ct.field);
  const op = TAG_OP_TO_CANONICAL[condition.operator] ?? '<';
  const help = compareHelp({
    lhsLabel,
    op,
    percent: ct.percent,
    rhsLabel,
    windowLabel: windowLabelOf(ct),
    inheritLabel: "the rule's own window",
  });

  return (
    <div className="rounded-lg border border-sky-500/30 bg-sky-500/5 px-4 py-3">
      <div className="flex items-end gap-3">
        <div className="min-w-[130px] max-w-[240px] flex-1">
          <label className={LABEL}>Metric</label>
          <select
            value={condition.field}
            onChange={(e) => onChange({ ...condition, field: e.target.value } as T)}
            className={SELECT}
            aria-label="Metric"
          >
            {fields.map((f) => (
              <option key={f.key} value={f.key}>{f.label}</option>
            ))}
          </select>
        </div>
        <div className="w-[84px]">
          <label className={LABEL}>Is</label>
          <select
            value={condition.operator}
            onChange={(e) => onChange({ ...condition, operator: e.target.value } as T)}
            className={SELECT}
            aria-label="Operator"
          >
            {OPERATORS.map((o) => (
              <option key={o.op} value={o.op}>{o.label}</option>
            ))}
          </select>
        </div>
        <PercentField
          percent={ct.percent}
          calcTitle={compareCalcTitle({
            lhsLabel,
            op,
            percent: ct.percent,
            rhsLabel,
            window: windowLabelOf(ct) ?? "the rule's own window",
          })}
          addTitle={`Add a % — e.g. 80 turns "${lhsLabel} vs ${rhsLabel}" into "${lhsLabel} vs 80% of ${rhsLabel}".`}
          onChange={(percent) => patchCt({ ...ct, percent })}
        />
        <div className="ml-auto flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={() => onChange({ ...condition, compareTo: undefined } as T)}
            title="Use a fixed value instead"
            aria-label="Use a fixed value instead"
            className="flex h-9 w-9 items-center justify-center rounded-md border border-dashed border-border text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary"
          >
            <Hash className="h-4 w-4" />
          </button>
          {canRemove && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onRemove}
              className="h-9 w-9 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
              aria-label="Remove condition"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
      <div className="mt-2">
        <CompareTargetGroup
          ct={ct}
          onPatch={patchCt}
          fields={fields}
          toggle={
            <AgainstToggle
              total={ct.against === 'total'}
              totalUnavailable={isDropScoped(ct.field)}
              onChange={(total) =>
                patchCt({ ...ct, against: total ? 'total' : undefined })
              }
            />
          }
        />
      </div>
      {ct.dateRangePreset === 'custom' && (
        <CompareCustomBounds ct={ct} onPatch={patchCt} />
      )}
      <CompareHelpLine help={help} className="mt-2 min-w-0" />
      <CompareOverrideTables
        countryThresholds={condition.countryThresholds}
        campaignThresholds={condition.campaignThresholds}
        onCountryChange={(rows) =>
          onChange({
            ...condition,
            countryThresholds: rows.length ? rows : undefined,
          } as T)
        }
        onCampaignChange={(rows) =>
          onChange({
            ...condition,
            campaignThresholds: rows.length ? rows : undefined,
          } as T)
        }
        fields={fields}
      />
    </div>
  );
}

export default CompareConditionRow;
